import React, { useState, useEffect } from 'react';
import { useAuth } from 'providers/AuthProvider'
import apiClient, { handleAPIError } from 'helpers/apiService';
import { Accordion, Col } from 'react-bootstrap';
import ReactEChartsCore from 'echarts-for-react/lib/core';
import * as echarts from 'echarts/core';
import { useAppContext } from 'providers/AppProvider';
import { TooltipComponent } from 'echarts/components';

echarts.use([TooltipComponent]);

type SingleClientTrendsData = {
    [contactName: string]: TrendsData
};

type TrendsData = {
    contactId: number;
    who_is_talking: number[];
    same_word: number[];
    common_language: number[];
    open_questions: number[];
    inserted_at: string[];
};

interface ITrendsData {
    props: TrendsData
};


const getDefaultOptions = (data: any, xLabels: string[], getThemeColor: (name: string) => string) => ({
    color: [
        '#e31710',  // rosso
        '#f4af36',  // giallo
        '#23a6b8',  // tiffany
        '#2e394b'   // grigio scuro
    ],
    tooltip: {
        trigger: 'axis',
        backgroundColor: getThemeColor('gray-soft'),
        borderColor: getThemeColor('gray-200'),
        /*
        formatter: (params: CallbackDataParams[]) =>
            tooltipFormatterDefault(params, 'MMM DD, YYYY', 'color'),
        */
        axisPointer: {
            shadowStyle: {
                color: 'red'
            }
        }
    },
    legend: {
        bottom: '10',
        data: [
            {
                name: 'Who is talking',
                icon: 'roundRect'
            },
            {
                name: 'Same words',
                icon: 'roundRect'
            },
            {
                name: 'Common Language',
                icon: 'roundRect'
            },
            {
                name: 'Open questions',
                icon: 'roundRect'
            }
        ],
        itemWidth: 16,
        itemHeight: 8,
        itemGap: 10,
        inactiveColor: getThemeColor('gray-500'),
        inactiveBorderWidth: 0,
        textStyle: {
            color: getThemeColor('gray-900'),
            fontWeight: 600,
            fontSize: 16,
            fontFamily: 'Nunito Sans'
        }
    },
    xAxis: [
        {
            show: true,
            interval: 2,
            axisLine: {
                lineStyle: {
                    type: 'solid',
                    color: getThemeColor('gray-300')
                }
            },
            axisLabel: {
                color: getThemeColor('gray-900'),
                interval: 5,
                align: 'left',
                margin: 20,
                fontSize: 12.8
            },
            axisTick: {
                show: true,
                length: 15
                // alignWithLabel: true
            },
            splitLine: {
                interval: 0,
                show: true,
                lineStyle: {
                    color: getThemeColor('gray-300'),
                    type: 'dashed'
                }
            },
            type: 'category',
            boundaryGap: false,
            data: [...xLabels, '31/12/2024']
        },
        {
            show: true,
            interval: 2,
            axisLine: {
                show: false
            },
            axisLabel: {
                show: false
            },
            axisTick: {
                show: false
            },
            splitLine: {
                interval: 1,
                show: true,
                lineStyle: {
                    color: getThemeColor('gray-300'),
                    type: 'solid'
                }
            },
            boundaryGap: false,
            data: [...xLabels, '31/12/2024']
        }
    ],
    yAxis: {
        show: true,
        type: 'value',
        axisLine: {
            lineStyle: {
                type: 'solid',
                color: getThemeColor('gray-300')
            }
        },
        axisLabel: {
            color: getThemeColor('gray-900'),
            margin: 20,
            fontSize: 12.8,
            interval: 0
        },
        splitLine: {
            show: true,
            lineStyle: {
                color: getThemeColor('gray-300'),
                type: 'solid'
            }
        },
        axisTick: {
            show: true,
            length: 15,
            alignWithLabel: true,
            lineStyle: {
                color: getThemeColor('gray-300')
            }
        }
        // data: ['0', '10', '20']
    },
    series: data,
    grid: {
        right: 5,
        left: 0,
        bottom: '15%',
        top: 20,
        containLabel: true
    }
});

const ClientChart = ({ props }: ITrendsData) => {

    //const Button = ({ children, onClick }: ButtonProps) => {    

    const { getThemeColor } = useAppContext();

    const data = [
        {
            name: 'Who is talking',
            type: 'line',
            symbolSize: 6,
            data: props.who_is_talking
        },
        {
            name: 'Same words',
            type: 'line',
            symbolSize: 6,
            data: props.same_word
        },
        {
            name: 'Common Language',
            type: 'line',
            symbolSize: 6,
            data: props.common_language
        },
        {
            name: 'Open questions',
            type: 'line',
            symbolSize: 6,
            data: props.open_questions
        }
    ];

    return (
        <ReactEChartsCore
            echarts={echarts}
            option={getDefaultOptions(data, props.inserted_at, getThemeColor)}
            style={{ minHeight: '320px', width: '100%' }}
        />
    );
};




const SingleClientTrend = () => {

    const auth = useAuth();
    const [data, setData] = useState<SingleClientTrendsData>();

    const fetchData = async () => {
        try {
            const response = await apiClient.get('/v1/myskills/singleclienttrends');
            const transformedData: SingleClientTrendsData = {};
            response.data.forEach((item: { [x: string]: any; contact_name: any; }) => {
                const { contact_name, ...rest } = item;
                if (!transformedData[contact_name]) {
                    transformedData[contact_name] = {
                        contactId: 0,
                        who_is_talking: [],
                        same_word: [],
                        common_language: [],
                        open_questions: [],
                        inserted_at: []
                    };
                }
                transformedData[contact_name].contactId = rest.contact_id;
                transformedData[contact_name].who_is_talking.push(rest.who_is_talking);
                transformedData[contact_name].same_word.push(rest.same_word);
                transformedData[contact_name].common_language.push(rest.common_language);
                transformedData[contact_name].open_questions.push(rest.open_questions);
                transformedData[contact_name].inserted_at.push(rest.inserted_at);
            });
            setData(transformedData);
        } catch (error) {
            console.error('Request failed:', error);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);


    return (
        <Accordion defaultActiveKey="0" className='mt-3' alwaysOpen >
            {data && Object.entries(data).map(([contactName, trendsData]) => (
                <Col xs={12} md="auto" key={trendsData.contactId}>
                    <Accordion.Item eventKey={contactName} className="border-top border-300" >
                        <Accordion.Header className="px-3  bg-white" >{contactName}</Accordion.Header>
                        <Accordion.Body className="d-flex align-items-center mb-2">
                            <ClientChart props={trendsData} />
                        </Accordion.Body>
                    </Accordion.Item>
                </Col>
            ))}
        </Accordion>
    );


};



export default SingleClientTrend;
